import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import { fmtMoney, fmtMonth, fmtPct, fmtSigned, fmtUnits, STATUS_LABEL } from "../format";
import type { Overview, SessionInfo, WorkType } from "../types";
import { DivergingBars, LineChart } from "./charts";

interface Props {
  config: SessionInfo["config"];
  workTypes: Record<string, WorkType>;
  notify: (msg: string) => void;
}

export default function Dashboard({ config, workTypes, notify }: Props) {
  const [family, setFamily] = useState("");
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [asTable, setAsTable] = useState(false);

  const load = useCallback(async (fam: string) => {
    setLoading(true);
    setError("");
    try { setData(await api.overview(fam)); }
    catch (e) { setError((e as Error).message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(family); }, [family, load]);

  const refresh = async () => {
    try { await api.refreshData(); await load(family); notify("Reloaded from Kinaxis."); }
    catch (e) { notify((e as Error).message); }
  };

  const k = data?.kpis;
  const hasBase = !!data?.baseline;

  return (
    <div className="dash">
      <div className="dash-inner">
        <div className="dash-head">
          <h1>Scenario impact</h1>
          <span className="chip">{config.scenario}{hasBase ? ` vs ${data!.baseline}` : ""}</span>
          <span className="grow" />
          <select className="input" style={{ width: 200 }} value={family} aria-label="Product family"
                  onChange={(e) => setFamily(e.target.value)}>
            <option value="">All families</option>
            {data?.families.map((f) => <option key={f}>{f}</option>)}
          </select>
          <button className="btn" onClick={refresh} disabled={loading}>Reload from Kinaxis</button>
        </div>

        {!hasBase && data && (
          <div className="callout warn small">No baseline scenario is configured (GENTIK_BASELINE_SCENARIO), so changes can't be shown against a "before".</div>
        )}
        {error && <div className="callout bad">{error}</div>}
        {loading && !data && <div className="row muted"><div className="spinner" /> Reading from Kinaxis…</div>}

        {data && k && (
          <>
            <div className="tiles">
              <div className="tile">
                <div className="label">Consensus, {config.scenario}</div>
                <div className="value">{fmtUnits(k.consensus_target)}</div>
                <div className="sub">units{k.horizon ? `, ${fmtMonth(k.horizon[0])} – ${fmtMonth(k.horizon[1])}` : ""}</div>
              </div>
              <div className="tile">
                <div className="label">Change vs baseline</div>
                <div className={`value ${(k.delta ?? 0) > 0 ? "pos" : (k.delta ?? 0) < 0 ? "neg" : ""}`}>{fmtSigned(k.delta)}</div>
                <div className="sub">{fmtPct(k.delta_pct)} units</div>
              </div>
              <div className="tile">
                <div className="label">Consensus value</div>
                <div className="value">{fmtMoney(k.value_target)}</div>
                <div className="sub">{k.value_baseline != null ? `${fmtSigned(Math.round(k.value_target - k.value_baseline))} USD vs baseline` : "at effective unit price"}</div>
              </div>
              <div className="tile">
                <div className="label">Intents</div>
                <div className="value">{fmtUnits(data.intents.total)}</div>
                <div className="sub">
                  {Object.entries(data.intents.by_status).map(([s, n]) => `${n} ${(STATUS_LABEL[s] ?? s).toLowerCase()}`).join(" · ") || "none yet"}
                </div>
              </div>
            </div>

            <div className="panels">
              <div className="panel wide">
                <div className="panel-head">
                  <h3>Consensus by month</h3>
                  <span className="muted small">{data.family ?? "all families"} · {fmtUnits(k.sku_count)} SKUs</span>
                  <button className="btn ghost sm right" onClick={() => setAsTable((v) => !v)}>{asTable ? "Chart" : "Table"}</button>
                </div>
                {asTable ? (
                  <table className="data">
                    <thead><tr><th>Month</th><th className="num">{config.scenario}</th>{hasBase && <><th className="num">{data.baseline}</th><th className="num">Change</th></>}</tr></thead>
                    <tbody>{data.monthly.map((p) => (
                      <tr key={p.month}><td>{fmtMonth(p.month)}</td><td className="num">{fmtUnits(p.target)}</td>
                        {hasBase && <><td className="num">{fmtUnits(p.baseline)}</td><td className={`num ${(p.delta ?? 0) >= 0 ? "pos" : "neg"}`}>{fmtSigned(p.delta)}</td></>}
                      </tr>))}
                    </tbody>
                  </table>
                ) : (
                  <LineChart height={280} labels={data.monthly.map((p) => p.month)} series={[
                    { name: "Target", color: "var(--series-target)", values: data.monthly.map((p) => p.target) },
                    ...(hasBase ? [{ name: "Baseline", color: "var(--series-base)", dashed: true, values: data.monthly.map((p) => p.baseline) }] : []),
                  ]} />
                )}
              </div>

              {hasBase && (
                <div className="panel">
                  <div className="panel-head"><h3>Change by family</h3><span className="muted small">units vs baseline</span></div>
                  <DivergingBars rows={data.by_family.filter((f) => f.delta != null).map((f) => ({
                    label: f.family, value: f.delta!, note: `${fmtPct(f.delta_pct)} · ${fmtUnits(f.target)} units in target`,
                  }))} />
                </div>
              )}

              {hasBase && (
                <div className="panel">
                  <div className="panel-head"><h3>Largest SKU changes</h3></div>
                  {data.movers.length === 0 ? <div className="empty">No SKU differs from the baseline yet.</div> : (
                    <table className="data">
                      <thead><tr><th>SKU</th><th>Family</th><th className="num">Baseline</th><th className="num">Target</th><th className="num">Change</th></tr></thead>
                      <tbody>{data.movers.map((m) => (
                        <tr key={m.sku}>
                          <td className="mono">{m.sku}</td><td>{m.family}</td>
                          <td className="num">{fmtUnits(m.baseline)}</td><td className="num">{fmtUnits(m.target)}</td>
                          <td className={`num ${m.delta >= 0 ? "pos" : "neg"}`}>{fmtSigned(m.delta)}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  )}
                </div>
              )}

              <div className="panel">
                <div className="panel-head"><h3>Intents by type of work</h3></div>
                {Object.keys(data.intents.by_work_type).length === 0 ? <div className="empty">No intents yet.</div> : (
                  <table className="data">
                    <tbody>{Object.entries(data.intents.by_work_type).sort((a, b) => b[1] - a[1]).map(([id, n]) => (
                      <tr key={id}><td>{workTypes[id]?.label ?? id}</td><td className="num">{n}</td></tr>
                    ))}</tbody>
                  </table>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
